import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, CalendarClock, Plus } from "lucide-react"
import { useRef, useState } from "react"
import { Link } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Planillas } from "@/componentes/gastos/Planillas"
import {
  AgendaProgramados,
  claveDe,
  DIAS_AGENDA,
  type DiasAgenda,
  type IndicadoresProgramados,
  TableroProgramados,
  type Vencimiento,
} from "@/componentes/gastos/TableroProgramados"
import { FichaOrden } from "@/componentes/ordenes/FichaOrden"
import { monto } from "@/componentes/tesoreria/dinero"
import { Boton } from "@/componentes/ui/Boton"
import { useConfirmar } from "@/componentes/ui/Confirmar"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { decimalOpcional } from "@/lib/decimales"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"
import { Campo } from "@/paginas/interno/Gastos"

type Frecuencia = "SEMANAL" | "MENSUAL" | "QUINCENAL" | "CADA_N_MESES" | "ANUAL" | "CADA_N_DIAS"

type Programado = {
  id: number
  gasto_id: number
  gasto: string | null
  proveedor_id: number
  proveedor: string | null
  concepto: string
  monto: string
  divisa_id: number
  divisa: string | null
  frecuencia: Frecuencia
  intervalo_meses: number
  intervalo_dias: number | null
  dia_pago: number | null
  dia_semana: number | null
  dias_antelacion: number
  proxima_fecha: string
  vigente_desde: string
  vigente_hasta: string | null
  activo: boolean
  planilla_id: number | null
}

/** Una planilla, como la enseña la lista. La ficha trae mucho más. */
export type Planilla = {
  id: number
  nombre: string
  descripcion: string | null
  activo: boolean
  lineas: number
}

type Gasto = { id: number; nombre: string; codigo: string; activo: boolean }

/** Lo que esta pantalla necesita de un contrato de nómina. Lo demás vive en su pestaña. */
type Contrato = {
  id: number
  persona: string | null
  proveedor: string | null
  sueldo_base: string
  divisa: string | null
  frecuencia: Frecuencia
  dia_pago: number | null
  dia_semana: number | null
  activo: boolean
}

type Resumen = {
  generadas: string[]
  repetidas: number
  sin_vencer: number
  caducadas: number
}

/** Cómo se lee cada frecuencia. El día se dice aparte, porque depende de ella. */
const FRECUENCIAS: { valor: Frecuencia; etiqueta: string }[] = [
  { valor: "SEMANAL", etiqueta: "Semanal" },
  { valor: "MENSUAL", etiqueta: "Mensual" },
  { valor: "QUINCENAL", etiqueta: "Quincenal" },
  { valor: "CADA_N_MESES", etiqueta: "Cada N meses" },
  { valor: "ANUAL", etiqueta: "Anual" },
  // ⚠ **Se desfasa del mes a propósito.** El personal de mantenimiento cobra cada 10
  // días: unos meses son tres pagos y otros cuatro, y forzarlo a semanal pagaría 4,3.
  { valor: "CADA_N_DIAS", etiqueta: "Cada N días" },
]

/**
 * «Día 5», «Último día», «Los 15 y el último»: la frase que hay que leer para saber
 * cuándo cae, en vez de dos columnas con un número y un enum que hay que combinar.
 */
/** Los días de la semana en ISO: el índice 1 es lunes, como `date.isoweekday()`. */
const DIAS_SEMANA = [
  { valor: 1, etiqueta: "Lunes" },
  { valor: 2, etiqueta: "Martes" },
  { valor: 3, etiqueta: "Miércoles" },
  { valor: 4, etiqueta: "Jueves" },
  { valor: 5, etiqueta: "Viernes" },
  { valor: 6, etiqueta: "Sábado" },
  { valor: 7, etiqueta: "Domingo" },
] as const

function nombreDelDia(dia: number | null): string {
  return DIAS_SEMANA.find((d) => d.valor === dia)?.etiqueta ?? "—"
}

function comoSeLee(p: {
  frecuencia: Frecuencia
  dia_pago: number | null
  dia_semana: number | null
  intervalo_meses?: number
  intervalo_dias?: number | null
}): string {
  const dia = p.dia_pago === null ? "el último día" : `el día ${p.dia_pago}`
  // La semanal no dice día del mes: cae en un día de la SEMANA, y es lo único que
  // hay que leer para saber cuándo toca.
  if (p.frecuencia === "SEMANAL") return `Cada ${nombreDelDia(p.dia_semana).toLowerCase()}`
  if (p.frecuencia === "CADA_N_DIAS") return `Cada ${p.intervalo_dias ?? 0} días`
  if (p.frecuencia === "QUINCENAL") return `Los ${p.dia_pago} y el último día`
  if (p.frecuencia === "CADA_N_MESES") return `Cada ${p.intervalo_meses ?? 1} meses, ${dia}`
  if (p.frecuencia === "ANUAL") return `Una vez al año, ${dia}`
  return `Cada mes, ${dia}`
}

/**
 * Gastos programados: lo que se paga todos los meses, con su calendario.
 *
 * ⚠ **Esto no aprueba ni paga nada.** Cuando toca, el sistema redacta la orden en
 * BORRADOR y avisa; el circuito de siempre —tres firmas y tesorería— no cambia.
 *
 * ⚠ **«Generar ahora» no duplica.** Dispara la misma pasada que el beat, y el periodo
 * ya generado lo rechaza el servidor: la respuesta dice cuántas salieron y cuántas ya
 * estaban.
 *
 * ⚠ **Aquí se ve TODO lo que se repite, incluidos los sueldos**, y ésa es la razón de
 * ser de la pantalla: la agencia tenía pagos recurrentes de personal en dos sitios —una
 * programación de gasto y un contrato de nómina—, y con cada lista contando la mitad no
 * había ningún sitio donde leer el compromiso del mes. Los sueldos salen **en sólo
 * lectura**, con su enlace: se editan en su pestaña, porque ahí viven sus conceptos y
 * sus recibos, y tener dos formularios para la misma fila es cómo se llega a dos
 * verdades.
 *
 * ⚠ **Los contratos sólo entran si quien mira tiene `nomina:ver`.** Sin él, la tabla y
 * las tarjetas son únicamente de gastos recurrentes, y la tarjeta lo dice.
 */
export function GastosProgramados() {
  const usuario = usarSesion((e) => e.usuario)
  // Lo que se exporta es la tabla de «Todo lo que se repite»: la agenda y las planillas
  // son tarjetas (la ficha de cada planilla trae su propio «Exportar»).
  const vista = useRef<HTMLDivElement | null>(null)
  const clienteQuery = useQueryClient()
  const [abierto, setAbierto] = useState(false)
  const [enEdicion, setEnEdicion] = useState<Programado | null>(null)
  const [resumen, setResumen] = useState<Resumen | null>(null)
  // Qué tarjeta se abrió. Ninguna cifra se queda sin poder explicarse.
  const [detalle, setDetalle] = useState<"mes" | "porDelante" | "generadas" | "series" | null>(null)
  const [ordenAbierta, setOrdenAbierta] = useState<number | null>(null)

  const puedeAdministrar = tienePermiso(usuario, "gasto_programado:administrar")
  const puedeGenerar = tienePermiso(usuario, "gasto_programado:generar")
  const puedeVerNomina = tienePermiso(usuario, "nomina:ver")
  const puedeProcesarNomina = tienePermiso(usuario, "nomina:procesar")
  const [confirmacion, confirmar] = useConfirmar()
  // Lo que dijo el último «Generar» de una fila de «Lo que viene».
  const [hechoFila, setHechoFila] = useState<{ ok: boolean; texto: string } | null>(null)

  const programados = useQuery({
    queryKey: ["gastos-programados"],
    queryFn: () => api.get<Programado[]>("/api/v1/gastos-programados?limit=100"),
  })

  // El tramo de «Próximos»: 7, 15 o 30 días (12/10). Se recuerda en el navegador.
  const [dias, setDias] = useState<DiasAgenda>(leerDias)
  const indicadores = useQuery({
    queryKey: ["gastos-programados", "indicadores", dias],
    queryFn: () =>
      api.get<IndicadoresProgramados>(`/api/v1/gastos-programados/indicadores?dias=${dias}`),
    placeholderData: keepPreviousData,
  })

  // Los sueldos, para la misma tabla. Cosmético como todo `tienePermiso`: sin el
  // permiso el servidor respondería 403 igual, y así no se pide para nada.
  const contratos = useQuery({
    queryKey: ["nomina-contratos", "activos"],
    // Sin `incluir_cerrados`: la ruta devuelve sólo los vivos, que es lo que se repite.
    queryFn: () => api.get<Contrato[]>("/api/v1/nomina/contratos"),
    enabled: puedeVerNomina,
  })

  const generar = useMutation({
    mutationFn: () => api.post<Resumen>("/api/v1/gastos-programados/generar", {}),
    onSuccess: (r) => {
      setResumen(r)
      clienteQuery.invalidateQueries({ queryKey: ["gastos-programados"] })
      clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
    },
  })

  /**
   * «Generar» UNA fila de «Lo que viene» (10/10): no esperar a las 8:15, o adelantar.
   *
   * ⚠ **La nómina genera su PERÍODO**: crea y aprueba la corrida de esa fecha, con una
   * orden en borrador por cada sueldo de esa frecuencia. Por eso se confirma antes.
   */
  const generarFila = useMutation({
    mutationFn: async (v: Vencimiento): Promise<string> => {
      if (v.origen === "GASTO") {
        const r = await api.post<Resumen>(
          `/api/v1/gastos-programados/${v.referencia_id}/generar`,
          {},
        )
        return `Redactada en borrador: ${r.generadas.join(", ")}.`
      }
      const p = await api.post<{ codigo: string }>("/api/v1/nomina/periodos/generar", {
        frecuencia: v.frecuencia,
        fecha_pago: v.fecha,
      })
      return `Nómina ${p.codigo} aprobada: sus órdenes de sueldo quedaron en borrador.`
    },
    onSuccess: (texto) => {
      setHechoFila({ ok: true, texto })
      clienteQuery.invalidateQueries({ queryKey: ["gastos-programados"] })
      clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
      clienteQuery.invalidateQueries({ queryKey: ["nomina-periodos"] })
    },
    onError: (e) =>
      setHechoFila({ ok: false, texto: e instanceof ErrorApi ? e.message : "No se pudo generar" }),
  })

  async function pedirGenerar(v: Vencimiento) {
    if (v.origen === "NOMINA") {
      const cual = (v.frecuencia ?? "").toLowerCase()
      const sigue = await confirmar({
        titulo: `Generar la nómina ${cual}`,
        mensaje: (
          <>
            Se genera y aprueba la nómina {cual} que se paga el{" "}
            <strong className="tabular-nums">{comoFecha(v.fecha)}</strong>: una orden en borrador
            por cada sueldo {cual} de esa fecha. No sale dinero: las órdenes siguen el circuito de
            firmas de siempre.
          </>
        ),
        aceptar: "Generar",
      })
      if (!sigue) return
    }
    setHechoFila(null)
    generarFila.mutate(v)
  }

  /** La próxima fecha de un sueldo sale de la agenda: el contrato no la guarda. */
  function proximaDeNomina(contratoId: number): string | null {
    const fila = indicadores.data?.agenda.find(
      (v) => v.origen === "NOMINA" && v.referencia_id === contratoId,
    )
    return fila?.fecha ?? null
  }

  function abrir(fila: Programado | null) {
    setEnEdicion(fila)
    setAbierto(true)
  }

  if (programados.isPending) {
    return <p className="py-6">Cargando…</p>
  }
  if (programados.isError) {
    return (
      <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} /> No se pudieron cargar los gastos programados.
      </p>
    )
  }

  const filas = programados.data

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-end gap-2">
        <Exportar
          titulo="Gastos programados"
          subtitulo="Todo lo que se repite: gastos programados y sueldos de nómina"
          contenido={vista}
        />
        {puedeGenerar && (
          <Boton onClick={() => generar.mutate()} disabled={generar.isPending}>
            <CalendarClock size={16} /> {generar.isPending ? "Generando…" : "Generar ahora"}
          </Boton>
        )}
        {puedeAdministrar && (
          <Boton variante="primario" onClick={() => abrir(null)}>
            <Plus size={16} /> Programar un gasto
          </Boton>
        )}
      </div>

      {indicadores.data && (
        <TableroProgramados
          datos={indicadores.data}
          onAbrir={setDetalle}
          onDias={(d) => {
            setDias(d)
            guardarDias(d)
          }}
        />
      )}

      {ordenAbierta !== null && (
        <FichaOrden ordenId={ordenAbierta} onCerrar={() => setOrdenAbierta(null)} />
      )}

      <Modal
        abierto={detalle !== null}
        titulo={detalle === null ? "" : TITULOS[detalle]}
        descripcion={detalle === null ? "" : DESCRIPCIONES[detalle]}
        onCerrar={() => setDetalle(null)}
        ancho="lg"
      >
        {detalle !== null && indicadores.data && (
          <DetalleDelTablero
            que={detalle}
            datos={indicadores.data}
            programados={filas}
            contratos={contratos.data ?? []}
            onAbrirOrden={(id) => {
              setDetalle(null)
              setOrdenAbierta(id)
            }}
          />
        )}
      </Modal>

      {/* ⚠ **La agenda comparte fila con las planillas.** Sola y a pantalla completa
          empujaba la tabla fuera de la vista: veinte filas de previsión no valen el
          alto que ocupaban. En un teléfono se apilan, que es lo único que cabe. */}
      <div className="grid gap-4 lg:grid-cols-5">
        {indicadores.data && (
          <AgendaProgramados
            datos={indicadores.data}
            className="lg:col-span-3"
            onGenerar={pedirGenerar}
            puedeGenerar={(v) => (v.origen === "NOMINA" ? puedeProcesarNomina : puedeGenerar)}
            generando={
              generarFila.isPending && generarFila.variables ? claveDe(generarFila.variables) : null
            }
          />
        )}
        <div className="lg:col-span-2">
          <Planillas />
        </div>
      </div>

      {hechoFila && (
        <p
          className="tarjeta text-sm"
          role={hechoFila.ok ? "status" : "alert"}
          style={hechoFila.ok ? undefined : { color: "var(--destructive)" }}
        >
          {hechoFila.texto}
        </p>
      )}

      {resumen && (
        <p className="tarjeta text-sm">
          {resumen.generadas.length === 0
            ? "No había nada que redactar todavía."
            : `Se redactaron en borrador: ${resumen.generadas.join(", ")}.`}
          {resumen.repetidas > 0 && ` ${resumen.repetidas} ya tenían la orden de su periodo.`}
        </p>
      )}

      <div ref={vista}>
        <Tabla etiqueta="Todo lo que se repite">
          <Cabecera>
            <Th>Concepto</Th>
            <Th>Origen</Th>
            <Th>A quién</Th>
            <Th>Cuándo</Th>
            <Th derecha>Monto</Th>
            <Th derecha>Próxima</Th>
            <Th>Estado</Th>
          </Cabecera>
          <Cuerpo>
            {filas.length === 0 && (contratos.data ?? []).length === 0 && (
              <TablaVacia colSpan={7}>
                Todavía no hay nada programado. El primero suele ser el alquiler.
              </TablaVacia>
            )}
            {filas.map((fila) => (
              <Fila
                key={`gasto-${fila.id}`}
                atenuada={!fila.activo}
                className={puedeAdministrar ? "cursor-pointer" : undefined}
                onClick={puedeAdministrar ? () => abrir(fila) : undefined}
              >
                <Td>{fila.concepto}</Td>
                <Td>
                  <Insignia tono="neutro">Gasto</Insignia>
                </Td>
                <Td>{fila.proveedor ?? "—"}</Td>
                <Td>{comoSeLee(fila)}</Td>
                <Td
                  derecha
                  numero
                  data-exportar-valor={`${monto(fila.monto)} ${fila.divisa ?? ""}`.trim()}
                >
                  {fila.monto} {fila.divisa ?? ""}
                </Td>
                <Td derecha numero>
                  {comoFecha(fila.proxima_fecha)}
                </Td>
                <Td>
                  {fila.activo ? (
                    <Insignia tono="exito">Activa</Insignia>
                  ) : (
                    <Insignia tono="neutro">Pausada</Insignia>
                  )}
                </Td>
              </Fila>
            ))}
            {/* Los sueldos, en sólo lectura: se editan en «Nómina», que es donde están
              sus conceptos y sus recibos. Dos formularios para la misma fila es cómo
              se llega a dos verdades. */}
            {(contratos.data ?? []).map((contrato) => {
              const proxima = proximaDeNomina(contrato.id)
              return (
                <Fila key={`nomina-${contrato.id}`}>
                  <Td>{contrato.persona ?? "—"}</Td>
                  <Td>
                    <Insignia tono="info">Nómina</Insignia>
                  </Td>
                  <Td>{contrato.proveedor ?? "—"}</Td>
                  <Td>{comoSeLee(contrato)}</Td>
                  <Td
                    derecha
                    numero
                    data-exportar-valor={`${monto(contrato.sueldo_base)} ${contrato.divisa ?? ""}`.trim()}
                  >
                    {contrato.sueldo_base} {contrato.divisa ?? ""}
                  </Td>
                  <Td derecha numero>
                    {proxima === null ? "—" : comoFecha(proxima)}
                  </Td>
                  <Td data-exportar-valor="Por nómina">
                    <Link
                      to="/talento-humano/contratos"
                      className="text-sm underline-offset-2 hover:underline"
                      style={{ color: "var(--primary-texto)" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Ver contrato
                    </Link>
                  </Td>
                </Fila>
              )
            })}
          </Cuerpo>
        </Tabla>
      </div>

      <Modal
        abierto={abierto}
        titulo={enEdicion ? `Editar ${enEdicion.concepto}` : "Programar un gasto"}
        descripcion="Cuando toque, el sistema redacta la orden en BORRADOR y avisa. Nada se aprueba ni se paga solo."
        onCerrar={() => setAbierto(false)}
      >
        {abierto && (
          <Formulario
            key={enEdicion?.id ?? "nuevo"}
            programado={enEdicion}
            onCerrar={() => setAbierto(false)}
            onGuardado={() => {
              clienteQuery.invalidateQueries({ queryKey: ["gastos-programados"] })
              setAbierto(false)
            }}
          />
        )}
      </Modal>
      {confirmacion}
    </section>
  )
}

function Formulario({
  programado,
  onCerrar,
  onGuardado,
}: {
  programado: Programado | null
  onCerrar: () => void
  onGuardado: () => void
}) {
  const usuario = usarSesion((e) => e.usuario)
  const [gastoId, setGastoId] = useState<number | null>(programado?.gasto_id ?? null)
  const [frecuencia, setFrecuencia] = useState<Frecuencia>(programado?.frecuencia ?? "MENSUAL")
  const [ultimoDia, setUltimoDia] = useState(programado ? programado.dia_pago === null : false)
  const [activo, setActivo] = useState(programado?.activo ?? true)
  const [planillaId, setPlanillaId] = useState<number | null>(programado?.planilla_id ?? null)

  const gastos = useQuery({
    queryKey: ["gastos"],
    queryFn: () => api.get<Gasto[]>("/api/v1/gastos?limit=100&activo=true"),
    enabled: tienePermiso(usuario, "gasto:ver"),
  })

  const planillas = useQuery({
    queryKey: ["planillas-programacion", "activas"],
    queryFn: () => api.get<Planilla[]>("/api/v1/planillas-programacion?activo=true"),
  })

  const guardar = useMutation({
    meta: { exito: "Programación guardada" },
    mutationFn: (cuerpo: Record<string, unknown>) =>
      programado
        ? api.patch(`/api/v1/gastos-programados/${programado.id}`, cuerpo)
        : api.post("/api/v1/gastos-programados", cuerpo),
    onSuccess: onGuardado,
  })

  function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const datos = new FormData(evento.currentTarget)
    const texto = (campo: string) => {
      const valor = String(datos.get(campo) ?? "").trim()
      return valor === "" ? null : valor
    }
    const numero = (campo: string) => {
      const valor = texto(campo)
      return valor === null ? null : Number(valor)
    }
    // La quincena necesita su primer día: el segundo ya es el último del mes, así que
    // «último día» no se ofrece para ella.
    const dia = ultimoDia && frecuencia !== "QUINCENAL" ? null : numero("dia_pago")
    const semanal = frecuencia === "SEMANAL"
    const cuerpo: Record<string, unknown> = {
      frecuencia,
      // ⚠ La semanal no tiene día del mes: manda `dia_semana` y deja `dia_pago` en
      // nulo. Mandar los dos guardaría un «día 5» que ninguna cuenta lee y que al
      // cambiar de frecuencia reaparecería como si alguien lo hubiera elegido.
      dia_pago: semanal ? null : dia,
      dia_semana: semanal ? numero("dia_semana") : null,
      intervalo_meses: frecuencia === "CADA_N_MESES" ? (numero("intervalo_meses") ?? 1) : 1,
      // ⚠ Nulo fuera de su frecuencia: un intervalo que sobrevive al cambio de
      // frecuencia reaparece como si alguien lo hubiera elegido, igual que el día.
      intervalo_dias: frecuencia === "CADA_N_DIAS" ? numero("intervalo_dias") : null,
      dias_antelacion: numero("dias_antelacion"),
      concepto: texto("concepto"),
      monto: decimalOpcional(texto("monto")),
      vigente_hasta: texto("vigente_hasta"),
      // Siempre, y `null` incluido: el servidor usa `exclude_unset`, así que mandarlo
      // en nulo es lo que la saca de su planilla. No mandarlo la dejaría donde estaba.
      planilla_id: planillaId,
    }
    if (programado) {
      cuerpo.activo = activo
    } else {
      // El gasto no se edita: cambiarlo sería otra programación. Y de él salen el
      // proveedor, la divisa y el monto de referencia.
      cuerpo.gasto_id = gastoId
      cuerpo.vigente_desde = texto("vigente_desde")
    }
    guardar.mutate(cuerpo)
  }

  const error = guardar.error instanceof ErrorApi ? guardar.error.message : null

  return (
    <form className="space-y-4" onSubmit={enviar}>
      {!programado && (
        <Campo
          para="gp-gasto"
          etiqueta="Gasto del catálogo"
          ayuda="De él salen el proveedor, la divisa y el monto, que luego se pueden ajustar aquí."
        >
          <SelectorBuscable
            id="gp-gasto"
            opciones={(gastos.data ?? []).map((g) => ({
              valor: g.id,
              etiqueta: g.nombre,
              detalle: g.codigo,
            }))}
            valor={gastoId}
            onCambiar={setGastoId}
            vacio="Elige el gasto"
            placeholder="Nombre o código…"
          />
        </Campo>
      )}

      <Campo
        para="gp-planilla"
        etiqueta="Planilla"
        ayuda="Opcional. Agrupa esta programación con otras para mirarlas y generarlas juntas."
      >
        <SelectorBuscable
          id="gp-planilla"
          opciones={(planillas.data ?? []).map((p) => ({
            valor: p.id,
            etiqueta: p.nombre,
            detalle: `${p.lineas} ${p.lineas === 1 ? "gasto" : "gastos"}`,
          }))}
          valor={planillaId}
          onCambiar={setPlanillaId}
          vacio="Sin planilla (suelta)"
          placeholder="Nombre de la planilla…"
        />
      </Campo>

      <Campo para="gp-concepto" etiqueta="Concepto" ayuda="Con lo que nace la orden cada vez.">
        <input
          id="gp-concepto"
          name="concepto"
          maxLength={200}
          defaultValue={programado?.concepto ?? ""}
          className="campo mt-1"
        />
      </Campo>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo para="gp-frecuencia" etiqueta="Frecuencia">
          <select
            id="gp-frecuencia"
            value={frecuencia}
            onChange={(e) => setFrecuencia(e.target.value as Frecuencia)}
            className="campo mt-1"
          >
            {FRECUENCIAS.map((f) => (
              <option key={f.valor} value={f.valor}>
                {f.etiqueta}
              </option>
            ))}
          </select>
        </Campo>

        {frecuencia === "CADA_N_DIAS" && (
          <Campo
            para="gp-intervalo-dias"
            etiqueta="Cada cuántos días"
            ayuda="El ciclo arranca en la fecha de vigencia, no en un día del mes: se desfasa a propósito."
          >
            <input
              id="gp-intervalo-dias"
              name="intervalo_dias"
              inputMode="numeric"
              defaultValue={programado?.intervalo_dias ?? 10}
              className="campo mt-1 tabular-nums"
            />
          </Campo>
        )}

        {frecuencia === "CADA_N_MESES" && (
          <Campo para="gp-intervalo" etiqueta="Cada cuántos meses">
            <input
              id="gp-intervalo"
              name="intervalo_meses"
              inputMode="numeric"
              defaultValue={programado?.intervalo_meses ?? 3}
              className="campo mt-1 tabular-nums"
            />
          </Campo>
        )}

        {/* La semanal cae en un día de la SEMANA, así que se elige de una lista y no
            se teclea un número: «5» sería viernes aquí y el día 5 del mes en las
            demás frecuencias, y esa ambigüedad es la que separa las dos columnas. */}
        {frecuencia === "CADA_N_DIAS" ? null : frecuencia === "SEMANAL" ? (
          <Campo
            para="gp-dia-semana"
            etiqueta="Día de la semana"
            ayuda="El borrador sale con la antelación de abajo, cada semana."
          >
            <select
              id="gp-dia-semana"
              name="dia_semana"
              defaultValue={programado?.dia_semana ?? 5}
              className="campo mt-1"
            >
              {DIAS_SEMANA.map((d) => (
                <option key={d.valor} value={d.valor}>
                  {d.etiqueta}
                </option>
              ))}
            </select>
          </Campo>
        ) : (
          <Campo
            para="gp-dia"
            etiqueta={frecuencia === "QUINCENAL" ? "Primer día de pago" : "Día de pago"}
            ayuda={
              frecuencia === "QUINCENAL"
                ? "El segundo pago es siempre el último día del mes."
                : "Un día que no exista en ese mes cae al último: el 31 en febrero es el 28."
            }
          >
            <input
              id="gp-dia"
              name="dia_pago"
              inputMode="numeric"
              disabled={ultimoDia && frecuencia !== "QUINCENAL"}
              defaultValue={programado?.dia_pago ?? 5}
              className="campo mt-1 tabular-nums"
            />
          </Campo>
        )}

        {frecuencia !== "QUINCENAL" && frecuencia !== "SEMANAL" && (
          <Campo para="gp-ultimo" etiqueta="O el último día del mes">
            <label className="mt-1 flex items-center gap-2 text-sm" htmlFor="gp-ultimo">
              <input
                id="gp-ultimo"
                type="checkbox"
                checked={ultimoDia}
                onChange={(e) => setUltimoDia(e.target.checked)}
              />
              Pagar el último día, sea 28, 30 o 31
            </label>
          </Campo>
        )}

        <Campo
          para="gp-antelacion"
          etiqueta="Días de antelación"
          ayuda="Con cuántos días antes quieres el borrador encima. Por omisión, 5."
        >
          <input
            id="gp-antelacion"
            name="dias_antelacion"
            inputMode="numeric"
            defaultValue={programado?.dias_antelacion ?? 5}
            className="campo mt-1 tabular-nums"
          />
        </Campo>

        <Campo
          para="gp-monto"
          etiqueta="Monto"
          ayuda="Manda éste, no el del catálogo. La orden nace en borrador y se ajusta a la factura."
        >
          <input
            id="gp-monto"
            name="monto"
            inputMode="decimal"
            defaultValue={programado?.monto ?? ""}
            className="campo mt-1 tabular-nums"
          />
        </Campo>

        {!programado && (
          <Campo para="gp-desde" etiqueta="Vigente desde" ayuda="Vacío es hoy.">
            <input id="gp-desde" name="vigente_desde" type="date" className="campo mt-1" />
          </Campo>
        )}
        <Campo para="gp-hasta" etiqueta="Vigente hasta" ayuda="Vacío: sin fecha de fin.">
          <input
            id="gp-hasta"
            name="vigente_hasta"
            type="date"
            defaultValue={programado?.vigente_hasta ?? ""}
            className="campo mt-1"
          />
        </Campo>
      </div>

      {programado && (
        <Campo para="gp-activo" etiqueta="Estado">
          <label className="mt-1 flex items-center gap-2 text-sm" htmlFor="gp-activo">
            <input
              id="gp-activo"
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
            />
            Activa
          </label>
          <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
            Pausar no borra nada. Al reactivar se sigue por el próximo vencimiento: lo que pasó
            mientras estuvo pausada no se genera.
          </span>
        </Campo>
      )}

      {error && (
        <p className="text-sm" style={{ color: "var(--destructive)" }}>
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Boton onClick={onCerrar}>Cancelar</Boton>
        <Boton type="submit" variante="primario" disabled={guardar.isPending}>
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </Boton>
      </div>
    </form>
  )
}

type QueDetalle = "mes" | "porDelante" | "generadas" | "series"

const TITULOS: Record<QueDetalle, string> = {
  mes: "Lo que vence este mes",
  porDelante: "Lo que viene",
  generadas: "Órdenes que salieron de aquí",
  series: "Las series vivas",
}

const DESCRIPCIONES: Record<QueDetalle, string> = {
  mes: "Fecha a fecha, incluido lo que ya pasó. Es lo que compone el compromiso del mes.",
  porDelante:
    "Desde hoy. Todavía no son órdenes: el borrador se redacta con la antelación de cada serie.",
  generadas: "Borradores redactados este mes por una programación. Pulsa uno para abrirlo.",
  series: "Lo que el sistema vigila: gastos programados y contratos de nómina.",
}

/**
 * Lo que hay detrás de cada tarjeta del tablero.
 *
 * ⚠ **Ninguna cifra se queda sin poder abrirse.** Un indicador que no se puede pulsar
 * obliga a adivinar qué lo compone, y entonces deja más dudas de las que aclara — que
 * es justo lo contrario de para lo que está.
 */
function DetalleDelTablero({
  que,
  datos,
  programados,
  contratos,
  onAbrirOrden,
}: {
  que: QueDetalle
  datos: IndicadoresProgramados
  programados: Programado[]
  contratos: Contrato[]
  onAbrirOrden: (ordenId: number) => void
}) {
  if (que === "generadas") {
    return (
      <Tabla etiqueta="Órdenes generadas este mes">
        <Cabecera>
          <Th>Orden</Th>
          <Th>Vencía</Th>
          <Th>Período</Th>
          <Th>Concepto</Th>
        </Cabecera>
        <Cuerpo>
          {datos.generadas.length === 0 && (
            <TablaVacia colSpan={4}>Este mes no se ha redactado ninguna.</TablaVacia>
          )}
          {datos.generadas.map((g) => (
            <Fila
              key={g.orden_id}
              className="cursor-pointer"
              onClick={() => onAbrirOrden(g.orden_id)}
            >
              <Td>{g.codigo}</Td>
              <Td numero>{comoFecha(g.vence_en)}</Td>
              <Td>{g.periodo}</Td>
              <Td>{g.concepto}</Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>
    )
  }

  if (que === "series") {
    return (
      <Tabla etiqueta="Series vivas">
        <Cabecera>
          <Th>Concepto</Th>
          <Th>Origen</Th>
          <Th>Cuándo</Th>
          <Th derecha>Monto</Th>
          <Th>Estado</Th>
        </Cabecera>
        <Cuerpo>
          {programados.map((p) => (
            <Fila key={`g-${p.id}`} atenuada={!p.activo}>
              <Td>{p.concepto}</Td>
              <Td>
                <Insignia tono="neutro">Gasto</Insignia>
              </Td>
              <Td>{comoSeLee(p)}</Td>
              <Td derecha numero>
                {p.monto} {p.divisa ?? ""}
              </Td>
              <Td>
                <Insignia tono={p.activo ? "exito" : "neutro"}>
                  {p.activo ? "Activa" : "Pausada"}
                </Insignia>
              </Td>
            </Fila>
          ))}
          {contratos.map((c) => (
            <Fila key={`n-${c.id}`}>
              <Td>{c.persona ?? "—"}</Td>
              <Td>
                <Insignia tono="info">Nómina</Insignia>
              </Td>
              <Td>{comoSeLee(c)}</Td>
              <Td derecha numero>
                {c.sueldo_base} {c.divisa ?? ""}
              </Td>
              <Td>
                <Insignia tono="exito">Activa</Insignia>
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>
    )
  }

  const lista: Vencimiento[] = que === "mes" ? datos.calendario : datos.agenda
  const cuantos = que === "mes" ? datos.pagos_del_mes : datos.pagos_por_delante
  return (
    <div className="space-y-2">
      {lista.length < cuantos && (
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Se enseñan {lista.length} de {cuantos}: la lista se corta para que quepa.
        </p>
      )}
      <Tabla etiqueta={TITULOS[que]}>
        <Cabecera>
          <Th>Fecha</Th>
          <Th>Concepto</Th>
          <Th>Origen</Th>
          <Th derecha>Monto</Th>
        </Cabecera>
        <Cuerpo>
          {lista.length === 0 && <TablaVacia colSpan={4}>No hay nada en ese tramo.</TablaVacia>}
          {lista.map((v) => (
            <Fila key={`${v.origen}-${v.referencia_id}-${v.fecha}`}>
              <Td numero>{comoFecha(v.fecha)}</Td>
              <Td>{v.concepto}</Td>
              <Td>
                <Insignia tono={v.origen === "NOMINA" ? "info" : "neutro"}>
                  {v.origen === "NOMINA" ? "Nómina" : "Gasto"}
                </Insignia>
              </Td>
              <Td derecha numero>
                {v.monto} {v.divisa}
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>
    </div>
  )
}

const CLAVE_DIAS = "setieca.programados.dias"

function leerDias(): DiasAgenda {
  try {
    const n = Number(localStorage.getItem(CLAVE_DIAS))
    return (DIAS_AGENDA as readonly number[]).includes(n) ? (n as DiasAgenda) : 30
  } catch {
    return 30
  }
}

function guardarDias(d: DiasAgenda): void {
  try {
    localStorage.setItem(CLAVE_DIAS, String(d))
  } catch {
    // No persiste; la sesión en curso lo respeta igual.
  }
}
